import { useEffect } from "react";
import { Layout } from "@/components/layout";
import { Home } from "lucide-react";
import { SEOHead } from "@/components/seo";

const FairHousing = () => {
  useEffect(() => {
    document.title = "Fair Housing Statement | Prime Realty Partners - New Jersey Real Estate";
  }, []);

  return (
    <Layout>
      <SEOHead
        title="Fair Housing Statement"
        description="Prime Realty Partners is committed to equal housing opportunities. Read our fair housing policy and learn about protected classes under federal law."
        canonicalUrl="https://www.prpsj.com/fair-housing"
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Fair Housing", url: "https://www.prpsj.com/fair-housing" }]}
      />

      <div className="container py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-primary/10 rounded-lg">
              <Home className="h-12 w-12 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold">
                Fair Housing Statement
              </h1>
              <p className="text-xl text-muted-foreground mt-2">
                Equal Opportunity Housing
              </p>
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-8">
            <p className="text-lg font-medium text-center">
              Prime Realty Partners is committed to providing equal housing opportunities 
              to all individuals regardless of race, color, religion, sex, national origin, 
              familial status, or disability.
            </p>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                The Fair Housing Act
              </h2>
              <p className="text-muted-foreground">
                The Fair Housing Act prohibits discrimination in the sale, rental, and financing 
                of housing based on race, color, national origin, religion, sex, familial status 
                (including children under the age of 18 living with parents or legal custodians, 
                pregnant women, and people securing custody of children under 18), and disability.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Protected Classes
              </h2>
              <p className="text-muted-foreground mb-4">
                Under the Fair Housing Act, it is illegal to discriminate based on:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Race</strong> - Any discrimination based on race or ethnicity</li>
                <li><strong>Color</strong> - Discrimination based on skin color</li>
                <li><strong>National Origin</strong> - Discrimination based on country of birth or ancestry</li>
                <li><strong>Religion</strong> - Discrimination based on religious beliefs or practices</li>
                <li><strong>Sex</strong> - Discrimination based on gender, including sexual harassment</li>
                <li><strong>Familial Status</strong> - Discrimination against families with children</li>
                <li><strong>Disability</strong> - Discrimination against individuals with physical or mental disabilities</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                Many states and localities have additional protected classes, including sexual orientation, 
                gender identity, source of income, and more.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Our Commitment
              </h2>
              <p className="text-muted-foreground mb-4">
                Prime Realty Partners and our affiliated agents are committed to:
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Treating all clients and customers with respect and dignity</li>
                <li>Providing equal professional service to all</li>
                <li>Making all properties available without discrimination</li>
                <li>Advertising in a non-discriminatory manner</li>
                <li>Complying with all federal, state, and local fair housing laws</li>
                <li>Ongoing fair housing education for all agents</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Filing a Complaint
              </h2>
              <p className="text-muted-foreground mb-4">
                If you believe you have experienced housing discrimination, you can file a complaint with:
              </p>
              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-semibold">U.S. Department of Housing and Urban Development (HUD)</h3>
                  <p className="text-muted-foreground">
                    Phone: 1-800-669-9777 (Voice) | 1-800-927-9275 (TTY)
                  </p>
                  <p className="text-muted-foreground">
                    Website: <a href="https://www.hud.gov/program_offices/fair_housing_equal_opp/online-complaint" 
                    className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                      www.hud.gov/fairhousing
                    </a>
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold">California Department of Fair Employment and Housing</h3>
                  <p className="text-muted-foreground">
                    Phone: 1-800-884-1684
                  </p>
                  <p className="text-muted-foreground">
                    Website: <a href="https://www.dfeh.ca.gov" 
                    className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                      www.dfeh.ca.gov
                    </a>
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-2xl font-semibold mb-4">
                Contact Us
              </h2>
              <p className="text-muted-foreground">
                If you have questions about our fair housing practices or wish to report a concern, 
                please contact us:
              </p>
              <div className="mt-4 text-muted-foreground">
                <p>Prime Realty Partners</p>
                <p>Email: info@prpsj.com</p>
                <p>Phone: (856) 210-3000</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default FairHousing;
